import { createEntityAdapter, type EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { produce } from 'immer';

import { type HttpRequestState, isDefined } from '@blaze-shared/shared-util';
import { type StyleSheet } from '@blaze-shared/theming-domain';

import { findMatchingStyleSheet } from './style.utils';
import * as StylesActions from './styles.actions';

export const STYLES_FEATURE_KEY = 'styles';

export interface State {
  load: HttpRequestState;
  create: HttpRequestState;
  update: HttpRequestState;
  delete: HttpRequestState;
  sheets: EntityState<StyleSheet>;
  projectId?: string;
}

const sheetsAdapter = createEntityAdapter<StyleSheet>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export interface StylesPartialState {
  readonly [STYLES_FEATURE_KEY]: State;
}

export const initialState: State = {
  load: { status: 'none' },
  create: { status: 'none' },
  update: { status: 'none' },
  delete: { status: 'none' },
  sheets: sheetsAdapter.getInitialState(),
};

export const reducer = createReducer(
  initialState,

  on(StylesActions.loadStylesSheets, (state) =>
    produce(state, (draft) => {
      draft.load.status = 'pending';
      draft.load.error = undefined;
    }),
  ),

  on(StylesActions.loadStyleSheetsSuccess, (state, { styleSheets, updateMode, projectId }) =>
    produce(state, (draft) => {
      draft.projectId = projectId;
      draft.sheets = updateMode
        ? sheetsAdapter.upsertMany(styleSheets, draft.sheets)
        : sheetsAdapter.setAll(styleSheets, draft.sheets);
      draft.load.status = 'success';
    }),
  ),

  on(StylesActions.loadStyleSheetsFailure, (state, { error }) =>
    produce(state, (draft) => {
      draft.load.status = 'failure';
      draft.load.error = error;
    }),
  ),

  on(StylesActions.createStyleSheet, (state) =>
    produce(state, (draft) => {
      draft.create.status = 'pending';
      draft.create.error = undefined;
    }),
  ),

  on(StylesActions.createStyleSheetSuccess, (state, { styleSheet }) =>
    produce(state, (draft) => {
      draft.sheets = sheetsAdapter.addOne(styleSheet, draft.sheets);
      draft.create.status = 'success';
    }),
  ),

  on(StylesActions.createStyleSheetFailure, (state, { error }) =>
    produce(state, (draft) => {
      draft.create.status = 'failure';
      draft.create.error = error;
    }),
  ),

  on(StylesActions.updateStyleSheetSuccess, (state, { styleSheet }) =>
    produce(state, (draft) => {
      draft.sheets = sheetsAdapter.upsertOne(styleSheet, draft.sheets);
      draft.update.status = 'success';
    }),
  ),

  on(StylesActions.updateStyleSheetFailure, (state, { error }) =>
    produce(state, (draft) => {
      draft.update.status = 'failure';
      draft.update.error = error;
    }),
  ),

  on(StylesActions.deleteStyleSheet, (state) =>
    produce(state, (draft) => {
      draft.delete.status = 'none';
      draft.delete.error = undefined;
    }),
  ),

  on(StylesActions.deleteStyleSheetSuccess, (state, { id }) =>
    produce(state, (draft) => {
      draft.delete.status = 'success';

      const styleSheet = draft.sheets.entities[id];
      if (!styleSheet) {
        return;
      }

      styleSheet.active = false;
    }),
  ),

  on(StylesActions.deleteStyleSheetFailure, (state, { error }) =>
    produce(state, (draft) => {
      draft.delete.status = 'failure';
      draft.delete.error = error;
    }),
  ),

  on(StylesActions.updateReferences, (state, { pageId, references }) =>
    produce(state, (draft) => {
      references.forEach((layoutRef) => {
        const sheets = draft.sheets.ids.map((id) => draft.sheets.entities[id]).filter(isDefined);

        const styleSheet = findMatchingStyleSheet({
          id: layoutRef.styleId,
          name: layoutRef.styleName,
          elementType: layoutRef.elementType,
          sheets,
        });

        if (!styleSheet) {
          return;
        }

        const toUpdate = styleSheet.references.find((styleRef) => styleRef.pageId === pageId);

        if (toUpdate && layoutRef.elementIds.length) {
          toUpdate.elementIds = layoutRef.elementIds;
        } else if (toUpdate) {
          const refIndex = styleSheet.references.findIndex((ref) => ref.pageId === pageId);
          styleSheet.references.splice(refIndex, 1);
        } else if (!toUpdate && layoutRef.elementIds.length) {
          styleSheet.references.push({
            pageId,
            elementIds: layoutRef.elementIds,
          });
        }
      });
    }),
  ),
);
